/**
 * The trip fund: contributions in, expenses out, and the pot they leave behind.
 *
 * One fund pays. `pot = Σ contributions − Σ expenses`, and there is no second party to divide with, so
 * no share, transfer, net position or debt appears here. The log is a **view of two tables**, not a
 * table: every figure below is read from the same rows in the same request that lists them, and the
 * running pot per row (`pot_after`) is computed in the read query, never stored.
 *
 * **`null` means unknown, never zero.** An empty fund is `pot_vnd: null` and `entry_count: 0` — "chưa
 * ghi gì" — never `0 đ`. `contributed_vnd` is null when no contribution row exists, and a participant
 * with no contributions has `contributed_vnd: null` ("chưa góp"). A `0` in any of these would read as a
 * measured nothing.
 *
 * **Integer đồng only.** `amount_vnd` is a `bigint` in the database and an integer here; nothing in the
 * money path rounds or divides. An expense is never clamped or refused against the fund's balance — it
 * may take `pot_vnd` negative, which is a fact to report, not an error.
 */

import { z } from "zod";

import { Id, IsoDateTime } from "./common.ts";

/** Which way the money went. The list is one chronological sequence of both kinds. */
export const FundEntryKind = z
  .enum(["contribution", "expense"])
  .meta({ id: "FundEntryKind", description: "`contribution` is money in; `expense` is money out." });

export type FundEntryKind = z.infer<typeof FundEntryKind>;

/**
 * One row of the ledger, whichever kind it is.
 *
 * A contribution carries `participant_id` and `contributor_name`; an expense carries `description`.
 * The fields the other kind does not use are `null` rather than absent, so one list can render both
 * without narrowing on a missing key.
 */
export const FundEntry = z
  .object({
    id: Id,
    kind: FundEntryKind.describe("Contribution or expense, in the order recorded."),
    participant_id: Id.nullable().describe("For a contribution, who paid in; null for an expense."),
    contributor_name: z
      .string()
      .nullable()
      .describe("The contributor's name, resolved at read time; null for an expense."),
    description: z.string().nullable().describe("What an expense bought; null for a contribution."),
    amount_vnd: z.number().int().describe("Integer đồng, always positive. The sign is the kind."),
    day_index: z
      .number()
      .int()
      .min(1)
      .nullable()
      .describe("The trip day this belongs to, or null for an entry with no day yet."),
    note: z.string().nullable().describe("Optional commentary. Never parsed for an amount."),
    created_at: IsoDateTime.describe("When the row was first recorded. A correction never moves it."),
    edited_at: IsoDateTime.nullable().describe("When the row was corrected, or null if it never was."),
    pot_after: z
      .number()
      .int()
      .describe(
        "The fund's balance after this row, computed in the read query over the whole ordered list. " +
          "Never stored: an entry inserted into an earlier day moves every later row's figure with it.",
      ),
  })
  .meta({ id: "FundEntry", description: "One contribution or expense, with the pot after it." });

export type FundEntry = z.infer<typeof FundEntry>;

/**
 * One person who may contribute, with what they have put in so far.
 *
 * A participant with no contributions has `contributed_vnd: null` — "chưa góp", not `0 đ`. A soft-
 * removed participant who has contributions keeps their total here (`removed: true`), so
 * `Σ contributed_vnd` over this list stays equal to `Σ contributions` exactly.
 */
export const FundParticipant = z
  .object({
    id: Id,
    name: z.string().describe("The trip-local name, with diacritics."),
    contributed_vnd: z
      .number()
      .int()
      .nullable()
      .describe("Total this person has contributed, or null when they have contributed nothing."),
    removed: z.boolean().describe("True once removed from the panel. Their recorded money is untouched."),
  })
  .meta({ id: "FundParticipant", description: "A contributor and their running total." });

export type FundParticipant = z.infer<typeof FundParticipant>;

/**
 * The whole fund as one read: the pot, the two sides of it, and every row in day-then-time order.
 *
 * The three totals are computed from the same rows as `entries`, in one query, so the header and the
 * list cannot disagree. `entry_count` is the number of rows, and no total is rounded or estimated.
 */
export const TripFundResponse = z
  .object({
    trip_id: Id,
    pot_vnd: z
      .number()
      .int()
      .nullable()
      .describe("Σ contributions − Σ expenses. Null when nothing is recorded — not 0. May be negative."),
    contributed_vnd: z.number().int().nullable().describe("Σ contributions, or null when none are recorded."),
    spent_vnd: z.number().int().nullable().describe("Σ expenses, or null when none are recorded."),
    entry_count: z.number().int().min(0).describe("How many ledger rows exist."),
    participants: z.array(FundParticipant),
    entries: z.array(FundEntry).describe("Day-ordered (nulls last), then by record order."),
  })
  .meta({ id: "TripFundResponse", description: "A trip's fund: totals, contributors and the ledger." });

export type TripFundResponse = z.infer<typeof TripFundResponse>;

/** `POST /trips/:id/fund/participants`. The folded name makes a duplicate a conflict, not a second person. */
export const CreateFundParticipantRequest = z
  .object({
    name: z.string().min(1).max(80).describe("The person's name as the trip knows it."),
  })
  .meta({ id: "CreateFundParticipantRequest", description: "Add a person who may contribute." });

export type CreateFundParticipantRequest = z.infer<typeof CreateFundParticipantRequest>;

/**
 * `PATCH /trips/:id/fund/participants/:participantId`. The name is the only editable field: a
 * participant is an identity in the trip, and what they contributed is recorded on the ledger rows, not
 * here. The new name is folded for the trip-local uniqueness check, so renaming onto an existing name is
 * the same 409 as adding one.
 */
export const UpdateFundParticipantRequest = z
  .object({
    name: z.string().min(1).max(80).describe("The person's corrected name, as the trip now knows it."),
  })
  .meta({ id: "UpdateFundParticipantRequest", description: "Rename a contributor." });

export type UpdateFundParticipantRequest = z.infer<typeof UpdateFundParticipantRequest>;

/** `POST /trips/:id/fund/contributions`. Money in, and who put it in. */
export const RecordFundContributionRequest = z
  .object({
    participant_id: Id.describe("Must be a participant of this trip."),
    amount_vnd: z.number().int().positive().describe("Integer đồng, greater than zero."),
    day_index: z.number().int().min(1).nullable().optional().describe("A trip day, when it matters; omit for none."),
    note: z.string().max(500).nullable().optional().describe("Optional commentary."),
  })
  .meta({ id: "RecordFundContributionRequest", description: "Record money into the fund." });

export type RecordFundContributionRequest = z.infer<typeof RecordFundContributionRequest>;

/** `POST /trips/:id/fund/expenses`. Money out of the fund. It names no payer: the fund's money is shared. */
export const RecordFundExpenseRequest = z
  .object({
    description: z.string().min(1).max(200).describe("What the money bought. Required, and the row's own name."),
    amount_vnd: z.number().int().positive().describe("Integer đồng, greater than zero."),
    day_index: z.number().int().min(1).nullable().optional().describe("A trip day, when it matters; omit for none."),
    note: z.string().max(500).nullable().optional().describe("Optional commentary."),
  })
  .meta({ id: "RecordFundExpenseRequest", description: "Record money out of the fund." });

export type RecordFundExpenseRequest = z.infer<typeof RecordFundExpenseRequest>;

/**
 * `PATCH /trips/:id/fund/contributions/:entryId` and `.../expenses/:entryId` — every field optional, an
 * omitted field left alone. A correction is not a financial event: it edits the row in place, so
 * `created_at` and the row's position never move, and `edited_at` is set.
 */
export const UpdateFundEntryRequest = z
  .object({
    amount_vnd: z.number().int().positive().optional().describe("The corrected amount, when it changed."),
    description: z
      .string()
      .min(1)
      .max(200)
      .optional()
      .describe("A corrected expense description. Ignored on a contribution."),
    day_index: z
      .number()
      .int()
      .min(1)
      .nullable()
      .optional()
      .describe("A corrected trip day; explicit null clears it."),
    note: z.string().max(500).nullable().optional().describe("A corrected note; explicit null clears it."),
  })
  .meta({ id: "UpdateFundEntryRequest", description: "Correct one ledger row in place." });

export type UpdateFundEntryRequest = z.infer<typeof UpdateFundEntryRequest>;
